import { apiUrl } from "../api/endpoints";
import React, { useState, useContext, useMemo, useEffect } from 'react';
import '../Components/modal.css';
import './edit.css';
import './DownloadReportShared.css';

import axios from 'axios';

import Select from 'react-select';

import { useAuth } from '../hooks/AuthProvider';
import Alert from '../Components/Alert';
import AiReportModePanel from '../Components/AiReportModePanel';
import AiReportVariablesStep from '../Components/AiReportVariablesStep';
import { LanguageContext } from '../context/LanguageContext';

import dashboardTranslations from '../languages/downloadReport.json';
import { getErrorMessage } from '../utils/errorUtils';
import { isReportDownloadDatastream, sortDatastreamsByReportPriority } from '../utils/datastreamValidation';
import {
  buildMissingAiReportParamsMessage,
  getAiReportTypeConfig,
  getMissingRequiredAiReportParams,
  mergeDefaultAiReportVariables,
  sanitizeAiReportVariablesForType,
  useAiReportTypes,
} from '../utils/aiReportTypes';
import {
  buildReportAccess,
  downloadFileFromUrl,
  getReportModeUi,
  isNoDataReportResponse,
  modeIncludesAiReport,
  REPORT_DOWNLOAD_MODES,
  resolveAiReportMode,
  resolveReportMode,
} from '../utils/reportDownload';
import { CustomSwitch } from '../svg/UtilsIcons';
import WorkDaysHoursPicker, { normalizeWorkDaysHours } from '../Components/WorkDaysHoursPicker';
import { resolveBrowserTimezone, zonedDateTimeToUnix } from '../utils/timezone';

import dayjs from 'dayjs';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';

const DEFAULT_NO_DATA_MESSAGE =
  'There is no data available for the selected filters and time range.';

export const DownloadTemplateReport = ({
  state,
  setState,
  template,
  setLoadingReport,
  onAlert,
}) => {
  const { permissions, email: authEmail, user } = useAuth();
  const { lang } = useContext(LanguageContext);

  const canDownloadStandardReport = permissions['download_dashboard_reports'];
  const canDownloadAiReport = permissions['download_ai_reports'];
  const t = dashboardTranslations[lang].downloadReport;
  const isRtl = ['ar', 'ur'].includes(lang);

  const isDarkTheme =
    document.documentElement.classList.contains('dark-theme') ||
    document.body.classList.contains('dark-theme');

  const pickerTheme = useMemo(
    () => createTheme({ palette: { mode: isDarkTheme ? 'dark' : 'light' } }),
    [isDarkTheme]
  );
  const { types: aiReportTypes, availability: aiReportAvailability } = useAiReportTypes();
  const aiReportsEnabledForTemplates = aiReportAvailability.templates !== false;
  const reportAccess = useMemo(
    () =>
      buildReportAccess({
        canDownloadStandardReport,
        canDownloadAiReport: canDownloadAiReport && aiReportsEnabledForTemplates,
      }),
    [aiReportsEnabledForTemplates, canDownloadAiReport, canDownloadStandardReport]
  );

  const [number, setNumber] = useState('');
  const [tstart, setTstart] = useState('');
  const [tstop, setTstop] = useState('');
  const [sendToEmail, setSendToEmail] = useState(false);
  const [email, setEmail] = useState('');
  const [selectedPins, setSelectedPins] = useState([]);
  const [selectedWorkDaysHours, setSelectedWorkDaysHours] = useState([]);
  const [fetchedWorkDaysHours, setFetchedWorkDaysHours] = useState([]);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [reportMode, setReportMode] = useState(reportAccess.defaultMode);
  const [aiReportVariables, setAiReportVariables] = useState({});
  const [showAiReportValidation, setShowAiReportValidation] = useState(false);

const defaultEmail = (authEmail || user?.email || '').trim();
const storedWorkDaysHours = useMemo(
  () => {
    const fromProps = normalizeWorkDaysHours(template?.work_days_hours || []);
    if (fromProps.length > 0) return fromProps;
    return normalizeWorkDaysHours(fetchedWorkDaysHours);
  },
  [template?.work_days_hours, fetchedWorkDaysHours]
);
const entityReportTimezone = template?.organization?.timezone || template?.organization_timezone || '';
const reportTimezone = entityReportTimezone || resolveBrowserTimezone();
const storedWorkDaysHoursKey = useMemo(
  () => JSON.stringify(storedWorkDaysHours),
  [storedWorkDaysHours]
);

useEffect(() => {
  if (sendToEmail) {
    setEmail((prev) => {
      if (prev.trim()) return prev;
      return defaultEmail;
    });
  }
}, [sendToEmail, defaultEmail]);
  useEffect(() => {
    if (state?.open) {
      setReportMode(reportAccess.defaultMode);
      setAiReportVariables(
        mergeDefaultAiReportVariables(aiReportTypes, template?.type || null, {}, { room: template?.name })
      );
      setSelectedWorkDaysHours(JSON.parse(storedWorkDaysHoursKey));
      setShowAiReportValidation(false);
    }
  }, [state?.open, reportAccess.defaultMode, aiReportTypes, template?.type, template?.name, storedWorkDaysHoursKey]);

  useEffect(() => {
    if (!state?.open || !template?.id) return undefined;
    let active = true;
    setScheduleLoading(true);
    axios
      .get(apiUrl(`template/get/${template.id}`))
      .then((response) => {
        if (!active) return;
        const rows = normalizeWorkDaysHours(response?.data?.data?.work_days_hours || []);
        setFetchedWorkDaysHours(rows);
        setSelectedWorkDaysHours(rows);
      })
      .catch((error) => {
        console.warn('[DownloadTemplateReport] Failed to load template work days/hours:', error);
      })
      .finally(() => {
        if (active) setScheduleLoading(false);
      });
    return () => {
      active = false;
    };
  }, [state?.open, template?.id]);

  const [alertState, setAlertState] = useState({
    open: false,
    message: '',
    type: 'info',
    position: isRtl ? 'left' : 'right',
  });

  const aggregationOptions = t.aggregationOptions;

  const showAlert = (type, message) => {
    if (typeof onAlert === 'function') {
      onAlert(type, message);
      return;
    }

    setAlertState({ open: true, type, message, position: isRtl ? 'left' : 'right' });
  };

  const closeReportDialog = () => setState({ open: false, id: 0 });

  const closeDialogWithAlert = (type, message) => {
    if (typeof onAlert === 'function') {
      closeReportDialog();
      onAlert(type, message);
      return;
    }

    showAlert(type, message);
    setTimeout(() => closeReportDialog(), 2000);
  };

  const filteredStreams = useMemo(() => {
    return sortDatastreamsByReportPriority(template?.datastreams).filter(isReportDownloadDatastream);
  }, [template]);

  const templateDatastreamOptions = useMemo(() => {
    return [
      { value: '__ALL__', label: t.allDatastreams || 'All Datastreams' },
      ...filteredStreams.map((ds) => ({
        value: ds.pin,
        label: `${ds.name} (${ds.pin})`,
      })),
    ];
  }, [filteredStreams, t.allDatastreams]);

  const selectedTemplateDatastreamOptions = useMemo(() => {
    if (selectedPins.length === filteredStreams.length && filteredStreams.length > 0) {
      return [{ value: '__ALL__', label: t.allDatastreams || 'All Datastreams' }];
    }

    return selectedPins
      .map((pin) => {
        const ds = filteredStreams.find((d) => d.pin === pin);
        return ds ? { value: pin, label: `${ds.name} (${pin})` } : null;
      })
      .filter(Boolean);
  }, [selectedPins, filteredStreams, t.allDatastreams]);

  const handleStreamChange = (selectedOptions) => {
    const safeOptions = selectedOptions || [];
    const isAllSelected = safeOptions.some((opt) => opt.value === '__ALL__');

    const pins = isAllSelected ? filteredStreams.map((ds) => ds.pin) : safeOptions.map((opt) => opt.value);
    setSelectedPins(pins);
  };

  const reportType = template?.type || null;
  const defaultAiReportRoom = String(template?.name || '').trim();

  useEffect(() => {
    if (!state?.open) return;
    setAiReportVariables((prev) =>
      mergeDefaultAiReportVariables(aiReportTypes, reportType, prev, { room: defaultAiReportRoom })
    );
  }, [state?.open, aiReportTypes, reportType, defaultAiReportRoom]);

  const resolvedReportMode = resolveReportMode(reportAccess, reportMode);
  const noReportPermission = reportAccess.noReportPermission;
  const missingAiReportParams = useMemo(
    () =>
      modeIncludesAiReport(resolvedReportMode)
        ? getMissingRequiredAiReportParams(aiReportTypes, reportType, aiReportVariables)
        : [],
    [aiReportTypes, aiReportVariables, reportType, resolvedReportMode]
  );
  const handleAiReportVariableChange = (fieldName, value) => {
    setAiReportVariables((prev) => ({ ...prev, [fieldName]: value }));
  };

  const handleDownload = async () => {
    if (noReportPermission) {
      return showAlert('error', t.noReportPermissionMessage || 'You do not have access to download reports.');
    }

    if (!number || isNaN(number) || number <= 0) return showAlert('error', t.alertInvalidStep);
    if (!tstart || !tstop) return showAlert('error', t.alertSelectTimes);

    const tstartUnix = zonedDateTimeToUnix(tstart, reportTimezone);
    const tstopUnix = zonedDateTimeToUnix(tstop, reportTimezone);

    if (tstartUnix === null || tstopUnix === null || tstartUnix >= tstopUnix) {
      return showAlert('error', t.alertTimeOrder);
    }

    if (sendToEmail) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!email || !emailRegex.test(email)) {
        return showAlert('error', t.alertInvalidEmail || 'Please enter a valid email address.');
      }
    }

    const payloadDatastreams = filteredStreams
      .filter((ds) => selectedPins.includes(ds.pin))
      .map((ds) => ({
        field: ds.pin,
        label: ds.name,
        min: ds.min,
        max: ds.max,
        dataType: ds.dataType ?? ds.datatype ?? ds.data_type,
      }));

    if (payloadDatastreams.length === 0) {
      return showAlert('error', t.errorSelectDatastreams || 'Please select at least one datastream.');
    }

    if (storedWorkDaysHours.length > 0 && selectedWorkDaysHours.length === 0) {
      return showAlert('error', t.alertSelectWorkDaysHours || 'Please select at least one configured work day/hour for this report.');
    }

    const shouldUseAiReport = resolveAiReportMode(reportAccess, reportMode);
    const aiTypeConfig = shouldUseAiReport ? getAiReportTypeConfig(aiReportTypes, reportType) : null;

    if (shouldUseAiReport && !aiTypeConfig) {
      return showAlert('error', 'Please choose an AI report type for this template before generating an AI report.');
    }

    const normalizedAiVariables = sanitizeAiReportVariablesForType(aiReportTypes, reportType, aiReportVariables);
    if (shouldUseAiReport) {
      const missingVars = getMissingRequiredAiReportParams(aiReportTypes, reportType, aiReportVariables);
      if (missingVars.length > 0) {
        setShowAiReportValidation(true);
        return showAlert(
          'error',
          buildMissingAiReportParamsMessage(missingVars, t.aiReportVariables)
        );
      }
    }

    const body = {
      bucket: 'Sensors',
      template_id: String(template.id),
      datastreams: payloadDatastreams,
      tstart: tstartUnix,
      tstop: tstopUnix,
      step: Number(number),
      sendToEmail,
      email,
      reportMode: resolvedReportMode,
      includeAiReport: shouldUseAiReport,
      aiReportType: reportType,
      aiReportVariables: normalizedAiVariables,
      work_days_hours: selectedWorkDaysHours,
      report_timezone: entityReportTimezone || undefined,
      tstart_local: tstart,
      tstop_local: tstop,
    };

    const reportModeUi = getReportModeUi(resolvedReportMode, t.reportModeUi);

    setLoadingReport(true);

    try {
      const response = await axios.post(apiUrl(`data/download_dashboard_report`), body);
      const responseData = response.data || {};

      if (isNoDataReportResponse(responseData)) {
        showAlert('info', responseData.message || t.noDataMessage || DEFAULT_NO_DATA_MESSAGE);
        return;
      }

      const { status, downloadUrl, reportMode: backendReportMode, downloadType } = responseData;
      const effectiveBackendMode =
        backendReportMode ||
        (downloadType === 'pdf' || /\.pdf($|\?)/i.test(String(downloadUrl || ''))
          ? REPORT_DOWNLOAD_MODES.AI
          : REPORT_DOWNLOAD_MODES.STANDARD);

      if (effectiveBackendMode !== resolvedReportMode) {
        throw new Error(
          'The report was generated in an unexpected format. Please try again.'
        );
      }

      if (sendToEmail) {
        closeDialogWithAlert(
          'success',
          `${reportModeUi.sendToEmailLabel} It will be sent to ${email} shortly.`
        );
      } else if (status === 'ready' && downloadUrl) {
        await downloadFileFromUrl(downloadUrl);
        closeDialogWithAlert('success', reportModeUi.successLabel);
      } else {
        closeDialogWithAlert('warning', t.reportPreparing);
      }
    } catch (error) {
      const errorMessage = getErrorMessage(
        error,
        `Could not generate the ${reportModeUi.nounLabel}. Please try again later.`
      );
      showAlert('error', errorMessage);
    } finally {
      setLoadingReport(false);
    }
  };

  if (!state?.open) return null;

  const maxDateTime = dayjs();
  const startValue = tstart ? dayjs(tstart) : null;
  const stopValue = tstop ? dayjs(tstop) : null;

  if (!reportAccess.canOpenReportDialog) {
    return (
      <div className="dr-modal" id="modal" dir={isRtl ? 'rtl' : 'ltr'}>
        <div className="dr-popup dr-popup--autoHeight" id="popup">
          <div className="dr-header modal-header">
            <div>
              <h2 className="dr-title">{t.permissionDeniedTitle}</h2>
              <p className="dr-subtitle">{t.permissionDeniedTemplateMessage}</p>
            </div>
          </div>

          <div className="dr-divider" />

          <div className="dr-content modal-content" />

          <div className="dr-footer modal-footer">
            <button className="btn btn-cancel" onClick={() => setState({ open: false, id: 0 })}>
              {t.buttonClose}
            </button>
            <div />
          </div>
        </div>
      </div>
    );
  }

  const primaryActionLabel = getReportModeUi(resolvedReportMode, t.reportModeUi).actionLabel;

  return (
    <React.Fragment>
      {typeof onAlert !== 'function' && (
        <Alert
          open={alertState.open}
          close={() => setAlertState({ ...alertState, open: false })}
          message={alertState.message}
          type={alertState.type}
          position={alertState.position}
        />
      )}

      <ThemeProvider theme={pickerTheme}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <div className="dr-modal" id="modal" dir={isRtl ? 'rtl' : 'ltr'}>
            <div className="dr-popup" id="popup">
              <div className="dr-header modal-header">
                <div>
                  <h2 className="dr-title">{t.titleTemplate}</h2>
                  <p className="dr-subtitle">{template?.name || 'Loading Template...'}</p>
                </div>
              </div>

              <div className="dr-divider" />

              <div className="dr-content modal-content">
                {noReportPermission && (
                  <div className="dr-permissionNotice" role="alert">
                    {t.noReportPermissionMessage || 'You do not have access to download this report. The download button is disabled.'}
                  </div>
                )}

                <div className="dr-field form-item">
                  <label className="dr-label form-lbl">{t.labelStart}</label>

                  <div className="dr-muiField">
                    <DateTimePicker
                      value={startValue}
                      format="DD/MM/YYYY hh:mm A"
                      onChange={(newVal) => {
                        setTstart(newVal ? dayjs(newVal).format('YYYY-MM-DDTHH:mm') : '');
                      }}
                      maxDateTime={maxDateTime}
                      ampm
                      slots={{ openPickerIcon: CalendarMonthIcon }}
                      slotProps={{
                        textField: { size: 'small', placeholder: t.placeholderDateTime },
                        popper: { className: 'dr-pickerPopper' },
                        actionBar: { actions: ['clear', 'cancel', 'accept'] },
                      }}
                    />
                  </div>
                </div>

                <div className="dr-field form-item">
                  <label className="dr-label form-lbl">{t.labelEnd}</label>

                  <div className="dr-muiField">
                    <DateTimePicker
                      value={stopValue}
                      format="DD/MM/YYYY hh:mm A"
                      onChange={(newVal) => {
                        setTstop(newVal ? dayjs(newVal).format('YYYY-MM-DDTHH:mm') : '');
                      }}
                      maxDateTime={maxDateTime}
                      ampm
                      slots={{ openPickerIcon: CalendarMonthIcon }}
                      slotProps={{
                        textField: { size: 'small', placeholder: t.placeholderDateTime },
                        popper: { className: 'dr-pickerPopper' },
                        actionBar: { actions: ['clear', 'cancel', 'accept'] },
                      }}
                    />
                  </div>
                </div>

                <div className="dr-field form-item">
                  <label className="dr-label form-lbl">{t.labelDatastreams}</label>

                  <Select
                    isMulti
                    isRtl={isRtl}
                    classNamePrefix="dr-select"
                    options={templateDatastreamOptions}
                    value={selectedTemplateDatastreamOptions}
                    onChange={handleStreamChange}
                    placeholder={t.placeholderSelectStreams}
                    isDisabled={!template}
                  />
                </div>

                <div className="dr-field form-item">
                  {storedWorkDaysHours.length > 0 ? (
                    <WorkDaysHoursPicker
                      mode="select"
                      value={selectedWorkDaysHours}
                      availableRows={storedWorkDaysHours}
                      onChange={setSelectedWorkDaysHours}
                      labels={{
                        title: t.workDaysHoursTitle || 'Report schedule',
                        reportWillUse: t.reportWillUse || 'Report will use',
                        available: t.availableWorkDaysHours || 'Available from template settings',
                      }}
                    />
                  ) : (
                    <div className="wdh wdh-default">
                      <div className="wdh-header">
                        <span>{t.workDaysHoursTitle || 'Report schedule'}</span>
                        <span className="wdh-summary">
                          {scheduleLoading ? 'Loading schedule...' : 'All days, 24 hours'}
                        </span>
                      </div>
                      <div className="wdh-available">
                        {scheduleLoading
                          ? 'Loading schedule...'
                          : 'No work days/hours are configured, so this report uses all days and 24 hours.'}
                      </div>
                    </div>
                  )}
                </div>

                <AiReportModePanel
                  access={reportAccess}
                  selectedMode={reportMode}
                  onModeChange={setReportMode}
                  labels={t.aiReportMode}
                />

                <AiReportVariablesStep
                  reportMode={resolvedReportMode}
                  reportType={reportType}
                  types={aiReportTypes}
                  variables={aiReportVariables}
                  onVariableChange={handleAiReportVariableChange}
                  missingFields={missingAiReportParams.map((param) => param.key)}
                  showValidation={showAiReportValidation}
                  labels={t.aiReportVariables}
                />

                <div className="dr-toggleRow form-item">
                  <label className="dr-label form-lbl">{t.labelSendEmail}</label>
                  <CustomSwitch checked={sendToEmail} onChange={(next) => setSendToEmail(next)} />
                </div>

                {sendToEmail && (
                  <div className="dr-field form-item">
                    <label className="dr-label form-lbl">{t.labelEmail || 'Email Address'}</label>
                    <input
                      type="email"
                      className="dr-input add-column"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder={t.placeholderEmail || 'Enter email address'}
                    />
                  </div>
                )}

                <div className="dr-field form-item">
                  <label className="dr-label form-lbl">{t.labelDataAggregation}</label>

                  <div className="dr-pillRow details-time-range">
                    {aggregationOptions.map((range, idx) => (
                      <span
                        key={idx}
                        className={`dr-pill details-control-btn ${number === range.value ? 'is-active active' : ''}`}
                        onClick={() => setNumber(range.value)}
                      >
                        {range.label}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              <div className="dr-footer modal-footer">
                <button className="btn btn-cancel" onClick={closeReportDialog}>
                  {t.buttonCancel}
                </button>

                <button
                  className="btn btn-apply"
                  onClick={handleDownload}
                  disabled={!template || noReportPermission}
                  title={noReportPermission ? (t.noReportPermissionMessage || 'You do not have access to download reports.') : undefined}
                >
                  {primaryActionLabel}
                </button>
              </div>
            </div>
          </div>
        </LocalizationProvider>
      </ThemeProvider>
    </React.Fragment>
  );
};
